import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, groupBy, uniq, without } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';
import { venomHistory } from 'client/utils/history/venom-history';
import { OPTION_TYPE, OPTIONS_RENDER_MAP } from 'site-modules/shared/constants/build-price';
import {
  getBuildPriceOptionsQuery,
  getColorPhoto,
  getCurrentExteriorColor,
  getOptionPrice,
  getReplacedColorIds,
} from 'site-modules/shared/utils/build-price/build-price';
import { Accordion } from 'site-modules/shared/components/accordion/accordion';
import { StickyStepHeading } from 'site-modules/build-price/components/sticky-step-heading/sticky-step-heading';
import { ColorSection } from './color-section';
import { OptionSection } from './option-section';
import { CalculateButton } from './calculate-button';

import './customize.scss';

const ACCORDION_TAGS_CONFIG = {
  accordionTag: 'div',
  fragmentTag: 'fieldset',
  headingTag: 'h3',
  labelTag: 'legend',
  contentTag: 'div',
};

function CustomizeUI({
  optionsConfig = null,
  optionsList = null,
  colorPhotos = null,
  buildPriceFilter,
  isMobile = false,
  modelYear,
  pathname,
}) {
  const optionsQueue = useRef([]);
  const { optionIds, urlOptionIds, styleId, styleFilter } = buildPriceFilter;
  const { makeName, modelName, year } = modelYear;
  const [sessionOptionIds, setSessionOptionIds] = useState(optionIds);

  const trackColorPhoto = useCallback(
    optionsIdsToTrack => {
      if (isMobile || !optionsList) {
        return;
      }

      const currentColor = getCurrentExteriorColor({ optionsList, optionIds: optionsIdsToTrack });
      const colorPhoto = getColorPhoto({ colorPhotos, optionsList, optionIds: optionsIdsToTrack });

      if (currentColor) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
          event_data: {
            action_name: TrackingConstant.ACTION_CONFIGURE_VEHICLE,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
            creative_id: 'step3_customize',
            make_name: makeName,
            model_name: modelName,
            model_year: year,
            value: `Exterior: ${currentColor}`,
            subaction_name: colorPhoto ? 'update_vehicle_image_color' : 'vehicle_image_color_unavailable',
          },
        });
      }
    },
    [colorPhotos, isMobile, makeName, modelName, optionsList, year]
  );

  useEffect(() => {
    trackColorPhoto(sessionOptionIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyOptionUpdate = useCallback(
    optionId => {
      const isSelected = !sessionOptionIds.includes(optionId);

      const additions = isSelected ? [optionId] : [];
      const removals = isSelected
        ? getReplacedColorIds([optionId], optionsList).filter(id => sessionOptionIds.includes(id))
        : [optionId];

      const optionIdsArray = urlOptionIds.split(',').filter(value => value);
      const replacedColors = getReplacedColorIds(
        additions,
        optionsList.filter(({ category }) => category === 'colors')
      );
      const nextOptionIds = uniq(without(optionIdsArray.concat(additions), ...removals.concat(replacedColors)));
      const nextSessionOptionIds = uniq(
        without(sessionOptionIds.split(',').concat(additions), ...removals.concat(replacedColors))
      );

      setSessionOptionIds(nextSessionOptionIds.join(','));

      setTimeout(() => {
        venomHistory.push(
          `${pathname}${getBuildPriceOptionsQuery({ styleFilter, styleId, optionIds: nextOptionIds.join(',') })}`
        );
      });
    },
    [optionsList, pathname, sessionOptionIds, styleFilter, styleId, urlOptionIds]
  );

  const optionHandled = useCallback(() => {
    optionsQueue.current.shift();

    if (optionsQueue.current.length) {
      applyOptionUpdate(optionsQueue.current[0]);
    }
  }, [applyOptionUpdate]);

  useEffect(() => {
    optionHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionIds]);

  const onOptionUpdate = useCallback(
    optionId => {
      optionsQueue.current.push(optionId);

      if (optionsQueue.current.length === 1) {
        applyOptionUpdate(optionsQueue.current[0]);
      }
    },
    [applyOptionUpdate]
  );

  const onColorOptionUpdate = useCallback(
    optionId => {
      trackColorPhoto([optionId]);
      onOptionUpdate(optionId);
    },
    [onOptionUpdate, trackColorPhoto]
  );

  if (!optionsConfig) {
    return null;
  }

  const { included } = optionsConfig;
  const hasColorPhoto = isMobile || !!getColorPhoto({ colorPhotos, optionsList, optionIds: sessionOptionIds });
  const renderFeatures = groupBy(optionsList, 'category');
  const includedOptions = included;
  // Can't define items which we can't unselect
  const requiredOptions = [];

  return (
    <Row className="customize justify-content-center" data-tracking-parent="step3_customize">
      <Col xs={12} lg={9}>
        <StickyStepHeading
          headingText="Select colors and options"
          stickyHeadingText={`Build your ${year} ${makeName} ${modelName}`}
          isMobile={isMobile}
        >
          <CalculateButton
            buildPriceFilter={buildPriceFilter}
            pathname={pathname}
            modelYear={modelYear}
            isMobile={isMobile}
            hasImage={isMobile}
          />
        </StickyStepHeading>

        <Accordion
          className="mb-2 mb-md-1"
          tabClasses="customize-tab py-0_75 py-md-1 heading-5 flex-row-reverse justify-content-between"
          itemClasses={null}
          iconCollapsed="icon-arrow-down4"
          iconExpanded="icon-arrow-up4"
          chevronClasses="ms-0_75 small text-primary-darker"
          tagsConfig={ACCORDION_TAGS_CONFIG}
          allowMultipleOpen
        >
          {OPTIONS_RENDER_MAP.map(({ id, title, path, type, trackingIdSuffix }) => {
            const currentOptions = get(renderFeatures, path, []).map(option => ({
              ...option,
              price: getOptionPrice(option, get(optionsConfig, 'optionPriceAdjustments')),
            }));
            const isColorSection = type === OPTION_TYPE.COLOR;
            if (!currentOptions.length) {
              return null;
            }

            return (
              <div
                key={`accordion-${id}`}
                id={id}
                label={title}
                trackingId={`build_price_expand_${trackingIdSuffix}`}
                isOpen={isColorSection}
              >
                {isColorSection ? (
                  <ColorSection
                    features={currentOptions}
                    optionIds={sessionOptionIds}
                    onOptionUpdate={onColorOptionUpdate}
                    hasColorPhoto={hasColorPhoto}
                  />
                ) : (
                  <OptionSection
                    features={currentOptions}
                    title={title}
                    optionIds={sessionOptionIds}
                    includedItems={includedOptions}
                    requiredItems={requiredOptions}
                    onOptionUpdate={onOptionUpdate}
                    trackingIdSuffix={trackingIdSuffix}
                  />
                )}
              </div>
            );
          }).filter(child => child)}
        </Accordion>
        {isMobile && (
          <div data-tracking-parent="step3_customize_inline">
            <CalculateButton
              buildPriceFilter={buildPriceFilter}
              pathname={pathname}
              modelYear={modelYear}
              isMobile={isMobile}
              hasImage={false}
            />
          </div>
        )}
      </Col>
    </Row>
  );
}

CustomizeUI.propTypes = {
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  modelYear: BuildPriceEntities.ModelYear.isRequired,
  pathname: PropTypes.string.isRequired,
  optionsList: PropTypes.arrayOf(BuildPriceEntities.Option),
  optionsConfig: BuildPriceEntities.OptionsConfig,
  colorPhotos: PropTypes.arrayOf(BuildPriceEntities.ColorPhoto),
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { optionsConfig }) => !optionsConfig;
export const Customize = connectToModel(React.memo(CustomizeUI, propsAreEqual), {
  optionsList: bindToPath(
    ({ buildPriceFilter: { styleId } }) => BuildPricePaths.buildStyleOptionsPath(styleId),
    BuildPriceModel
  ),
  optionsConfig: bindToPath(
    ({ buildPriceFilter: { styleId, optionIds } }) =>
      BuildPricePaths.buildConfigurationWithOptionsPath(styleId, optionIds),
    BuildPriceModel
  ),
  colorPhotos: bindToPath(
    ({ modelYear: { makeSlug, modelSlug, year, modelName }, buildPriceFilter: { styleFilter }, isMobile }) =>
      !isMobile &&
      BuildPricePaths.buildColorPhotosPath({
        make: makeSlug,
        model: modelSlug,
        year,
        submodel: `${modelName} ${styleFilter.submodel}`,
      }),
    BuildPriceModel
  ),
});
